import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Helper function for currency formatting
const formatCurrency = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
};

// Table component used by EVATab
const Table = ({ headers, keys, data, formatters, boldRows = [] }) => (<div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"><thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{headers.map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{h}</th>)}</tr></thead><tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">{data.map((row, rowIndex) => (<tr key={rowIndex} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${boldRows.includes(rowIndex) ? 'font-bold' : ''}`}>{keys.map(key => (<td key={key} className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatters[key] ? formatters[key](row[key]) : row[key]}</td>))}</tr>))}</tbody></table></div>);

// Explanation component used by EVATab
const Explanation = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
        <div className="mt-8 border-t-2 border-gray-200 dark:border-gray-700 explanation-section">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
                <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h4>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-4 animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

// Main EVATab component
const EVATab = ({ outputs }) => (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Economic Value Added (EVA)</h2><Table headers={['Year', 'EVA']} keys={['year', 'eva']} data={outputs.evaStatement || []} formatters={{ 'eva': formatCurrency }} />
    <Explanation title="How This Works">
        <div className="space-y-4">
            <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3">📊 Economic Value Added (EVA) Table</h5>
            <p><strong>Year:</strong> Project timeline showing the progression over the project's operational life.</p>
            <p><strong>EVA:</strong> Economic Value Added calculation showing value creation/destruction for each year.</p>
            
            <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3 mt-6">🔍 Comprehensive EVA Analysis</h5>
            <p><strong>What EVA measures:</strong> Residual wealth after deducting cost of capital from operating profit. Unlike traditional accounting profit, EVA considers the true cost of all capital employed.</p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                <p><strong>EVA Formula:</strong> NOPAT - (Capital Employed × WACC)</p>
                <ul className="mt-2 space-y-1 text-sm">
                    <li><strong>NOPAT:</strong> Net Operating Profit After Tax</li>
                    <li><strong>WACC:</strong> Weighted Average Cost of Capital (using the discount rate)</li>
                    <li><strong>Capital Employed:</strong> Total invested capital in the project</li>
                </ul>
            </div>
            
            <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3 mt-6">📈 Value Interpretation</h5>
            <ul className="space-y-2">
                <li><strong>Positive EVA:</strong> Value creation - Project generates returns above the cost of capital</li>
                <li><strong>Negative EVA:</strong> Value destruction - Project fails to cover its capital costs</li>
                <li><strong>Zero EVA:</strong> Break-even - Project exactly covers its cost of capital</li>
            </ul>
            
            <p><strong>Why it's important:</strong> More comprehensive than accounting profit as it considers capital costs. Traditional profit ignores the opportunity cost of capital, while EVA provides a true measure of economic value creation.</p>
            
            <h5 className="font-bold text-gray-700 dark:text-gray-300 mb-3 mt-6">💰 Why EVA Matters</h5>
            <p>The EVA analysis is crucial for understanding:</p>
            <ul className="space-y-2 mt-2">
                <li><strong>True economic value creation</strong> beyond accounting profits</li>
                <li><strong>Capital efficiency</strong> - how well the project uses invested capital</li>
                <li><strong>Performance vs. cost of capital</strong> - whether returns exceed financing costs</li>
                <li><strong>Value-based decision making</strong> for investors and stakeholders</li>
                <li><strong>Comparative analysis</strong> across different project scenarios</li>
            </ul>
            
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-700 mt-4">
                <p><strong>Goal:</strong> To provide a performance metric that accurately reflects whether the solar project is creating or destroying economic value, helping investors make informed decisions about capital allocation and project viability.</p>
            </div>
        </div>
    </Explanation>
</div>);

export default EVATab; 